import { useMemo } from 'react';

import {
  isType,
  useResourceDisplayNamesQuery,
} from '@spotify/confidence-graphql';

import {
  type GroupedResourceNames,
  collectResourceNames,
  getResourceType,
  hasAnyResourceNames,
  isResourceName,
} from './resourceNames';

const PLACEHOLDER_FILTER = 'name:doesnotexist';

export const buildNameFilter = (names: string[]): string => {
  if (names.length === 0) return PLACEHOLDER_FILTER;
  return `name:(${names.map(n => `"${n}"`).join(' OR ')})`;
};

export const buildDisplayNameMap = (
  data: ReturnType<typeof useResourceDisplayNamesQuery>['data'],
): Map<string, string> => {
  const map = new Map<string, string>();
  if (!data) return map;

  if (isType(data.assignmentTables, 'MetricsV1ListAssignmentTablesResponse')) {
    for (const table of data.assignmentTables.assignmentTables) {
      map.set(table.name, table.displayName);
    }
  }
  if (isType(data.entities, 'MetricsV1ListEntitiesResponse')) {
    for (const entity of data.entities.entities) {
      map.set(entity.name, entity.displayName);
    }
  }
  if (isType(data.factTables, 'MetricsV1ListFactTablesResponse')) {
    for (const table of data.factTables.factTables) {
      map.set(table.name, table.displayName);
    }
  }
  if (isType(data.flags, 'FlagsAdminV1ListFlagsResponse')) {
    for (const flag of data.flags.flags) {
      map.set(flag.name, flag.displayName);
    }
  }
  if (isType(data.metrics, 'MetricsV1ListMetricsResponse')) {
    for (const metric of data.metrics.metrics) {
      map.set(metric.name, metric.displayName);
    }
  }
  if (isType(data.surfaces, 'WorkflowV1ListSurfacesResponse')) {
    for (const surface of data.surfaces.surfaces) {
      map.set(surface.name, surface.displayName);
    }
  }

  return map;
};

export const useResourceDisplayNames = (
  input: unknown,
  additionalNames?: string[],
): { resolvedNames: Map<string, string>; loading: boolean } => {
  const grouped: GroupedResourceNames = useMemo(() => {
    const result = collectResourceNames(input);
    if (additionalNames) {
      for (const name of additionalNames) {
        if (isResourceName(name)) {
          const type = getResourceType(name);
          if (!result[type].includes(name)) {
            result[type].push(name);
          }
        }
      }
    }
    return result;
  }, [input, additionalNames]);

  const shouldFetch = hasAnyResourceNames(grouped);

  const { data, loading } = useResourceDisplayNamesQuery({
    skip: !shouldFetch,
    variables: {
      assignmentTablesFilter: buildNameFilter(grouped.assignmentTables),
      entitiesFilter: buildNameFilter(grouped.entities),
      factTablesFilter: buildNameFilter(grouped.factTables),
      flagsFilter: buildNameFilter(grouped.flags),
      metricsFilter: buildNameFilter(grouped.metrics),
      surfacesFilter: buildNameFilter(grouped.surfaces),
      fetchAssignmentTables: grouped.assignmentTables.length > 0,
      fetchEntities: grouped.entities.length > 0,
      fetchFactTables: grouped.factTables.length > 0,
      fetchFlags: grouped.flags.length > 0,
      fetchMetrics: grouped.metrics.length > 0,
      fetchSurfaces: grouped.surfaces.length > 0,
    },
  });

  const resolvedNames = useMemo(() => buildDisplayNameMap(data), [data]);

  return { resolvedNames, loading };
};
